/* eslint-disable react-refresh/only-export-components -- co-locating Provider + hook, matches references-context.tsx */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';

import {
  buildReferenceRowIndex,
  getReferenceRowFromIndex,
  type ReferenceRowIndex,
} from '@/lib/reference-row-index';
import type { EditLinkRefRow } from '@/lib/resolve-edit-link-open';

export type ReferenceRowLookup = {
  /** O(1) lookup of a single row by (sourceBlockId, chip token id). */
  getReferenceRow: (
    sourceId: string,
    tokenId: string,
  ) => EditLinkRefRow | undefined;
  /**
   * Live getter for the full references array. Read lazily — at
   * click/picker-open time — never subscribed to during render, so editors
   * don't each mount their own `useReferencesQuery` observer.
   */
  getReferences: () => EditLinkRefRow[] | undefined;
};

const noopLookup: ReferenceRowLookup = {
  getReferenceRow: () => undefined,
  getReferences: () => undefined,
};

const ReferenceRowIndexContext =
  createContext<ReferenceRowLookup>(noopLookup);

export function ReferenceRowIndexProvider({
  rows,
  children,
}: {
  rows: EditLinkRefRow[] | undefined;
  children: ReactNode;
}) {
  const rowsRef = useRef(rows);
  const indexRef = useRef<ReferenceRowIndex<EditLinkRefRow> | null>(null);
  if (indexRef.current === null) {
    indexRef.current = buildReferenceRowIndex(rows);
  }

  // Rebuilt only when the query-data identity actually changes (TanStack
  // Query keeps the same array reference across re-renders that don't touch
  // this cache entry) — not on every render of every consumer. Refs are
  // written from an effect (not render) per the `react-hooks/refs` rule;
  // `getReferenceRow`/`getReferences` are only ever called from event
  // handlers (picker-open, add-reference), which always run after effects
  // have flushed, so there's no read-before-write lag in practice.
  useEffect(() => {
    rowsRef.current = rows;
    indexRef.current = buildReferenceRowIndex(rows);
  }, [rows]);

  // Stable function identities across the provider's lifetime — safe for
  // editors to capture once (e.g. inside an EditorJS tool's `onPickerOpen`
  // closure created at mount) and call repeatedly without re-subscribing.
  const value = useMemo<ReferenceRowLookup>(
    () => ({
      getReferenceRow: (sourceId, tokenId) => {
        // Lazily initialized above and only ever reassigned (never cleared)
        // by the sync effect — never null once the provider has rendered.
        const index = indexRef.current;
        return index
          ? getReferenceRowFromIndex(index, sourceId, tokenId)
          : undefined;
      },
      getReferences: () => rowsRef.current,
    }),
    [],
  );

  return (
    <ReferenceRowIndexContext.Provider value={value}>
      {children}
    </ReferenceRowIndexContext.Provider>
  );
}

export function useReferenceRowLookup(): ReferenceRowLookup {
  return useContext(ReferenceRowIndexContext);
}
